import { useCallback, useEffect, useRef, useState } from 'react'
import {
  createGranularPreview,
  GRANULAR_PRESETS,
  type GranularPreview as Preview,
} from '@/lib/granular'
import type { SampleSlice } from '@/hooks/useSampleUpload'

type PreviewMode = 'chopper' | 'stretcher'

/**
 * Tempera-style granular preview for the slicer. The user picks a
 * position in the sample, sets grain size + overlap + pitch, and
 * hits Play to hear the granulated result. When the sample has
 * slices and "Snap to slices" is on, the position slider quantises
 * to the nearest slice start — which is the user's stated take on
 * "quantize the grains for accurate drum chopping" rather than
 * letting the grain trigger drift between slices.
 *
 * State + lifecycle live entirely in this component:
 *   - one Preview instance per (buffer) tuple
 *   - tearing it down on buffer change OR unmount
 *   - the slider drags can fire updates at >60Hz; we throttle by
 *     only applying changes when params actually differ
 *
 * Bundle cost lives upstream — `tone` is already imported by
 * granular.ts and lazy code-splitting by route is handled by Vite.
 */

interface GranularPreviewProps {
  buffer: AudioBuffer | null
  durationSec: number | undefined
  /** Optional slice list. When present + snapToSlices is on, the
   *  position slider quantises to slice starts. */
  slices?: SampleSlice[]
}

export function GranularPreview({
  buffer,
  durationSec,
  slices,
}: GranularPreviewProps) {
  const [mode, setMode] = useState<PreviewMode>('stretcher')
  const [positionSec, setPositionSec] = useState(0)
  const [grainSizeMs, setGrainSizeMs] = useState(100)
  const [overlapPct, setOverlapPct] = useState(50)
  const [pitchSemitones, setPitchSemitones] = useState(0)
  // 0.25 – 4.0 multiplier, stored as percent (25 – 400) so the
  // slider integer step is intuitive. Defaults to 100 (native).
  const [speedPct, setSpeedPct] = useState(100)
  const [loop, setLoop] = useState(true)
  const [snapToSlices, setSnapToSlices] = useState(true)
  const [playing, setPlaying] = useState(false)

  // Mode switch applies a preset from `GRANULAR_PRESETS` so the user
  // can flip between the two useful personalities of the GrainPlayer
  // without having to dial every slider by hand. Chopper = tight
  // grains + no overlap for transient-safe drum work; Stretcher =
  // longer grains + overlap + loop for conform-to-tempo auditioning.
  const applyMode = useCallback((next: PreviewMode) => {
    setMode(next)
    const preset = GRANULAR_PRESETS[next]
    if (preset.grainSize !== undefined) {
      setGrainSizeMs(Math.round(preset.grainSize * 1000))
    }
    if (preset.overlap !== undefined) {
      // `overlap` in the store is a fraction of grainSize (0..1).
      setOverlapPct(Math.round(preset.overlap * 100))
    }
    if (preset.playbackRate !== undefined) {
      setSpeedPct(Math.round(preset.playbackRate * 100))
    }
    if (preset.loop !== undefined) {
      setLoop(preset.loop)
    }
  }, [])

  const previewRef = useRef<Preview | null>(null)

  // Tear down + recreate the Preview whenever the source buffer
  // changes (user picked a different sample). Doing this in an
  // effect keeps the (sync) render pure.
  useEffect(() => {
    if (!buffer) {
      previewRef.current?.dispose()
      previewRef.current = null
      setPlaying(false)
      return
    }
    let cancelled = false
    let created: Preview | null = null
    void (async () => {
      const p = await createGranularPreview(buffer)
      if (cancelled) {
        p.dispose()
        return
      }
      previewRef.current = p
      created = p
    })()
    return () => {
      cancelled = true
      if (created) created.dispose()
      previewRef.current = null
      setPlaying(false)
    }
  }, [buffer])

  // Quantize position to the nearest slice start when snap is on.
  // Useful for drum-loop chopping — without this, scrubbing the
  // position slider through a break sounds like "granular
  // insanity" (chaotic mid-transient retriggers); with it, every
  // grain trigger lands on a known rhythmic boundary.
  const snapPosition = useCallback(
    (pos: number): number => {
      if (!snapToSlices || !slices || slices.length === 0) return pos
      let best = slices[0].startSec
      let bestDelta = Math.abs(pos - best)
      for (const s of slices) {
        const d = Math.abs(pos - s.startSec)
        if (d < bestDelta) {
          best = s.startSec
          bestDelta = d
        }
      }
      return best
    },
    [snapToSlices, slices],
  )

  const currentParams = useCallback(
    () => ({
      positionSec: snapPosition(positionSec),
      grainSize: grainSizeMs / 1000,
      overlap: (overlapPct / 100) * (grainSizeMs / 1000),
      pitchSemitones,
      playbackRate: speedPct / 100,
      loop,
    }),
    [
      positionSec,
      grainSizeMs,
      overlapPct,
      pitchSemitones,
      speedPct,
      loop,
      snapPosition,
    ],
  )

  // Push live param changes to the GrainPlayer while playing — so
  // the user hears slider drags update in real time without having
  // to stop+start.
  useEffect(() => {
    if (playing && previewRef.current) {
      previewRef.current.update(currentParams())
    }
  }, [playing, currentParams])

  const handlePlay = useCallback(() => {
    if (!previewRef.current) return
    previewRef.current.play(currentParams())
    setPlaying(true)
  }, [currentParams])

  const handleStop = useCallback(() => {
    previewRef.current?.stop()
    setPlaying(false)
  }, [])

  const max = durationSec ?? 0

  return (
    <section
      aria-label="Granular preview"
      className="rounded-lg bg-surface-raised border border-brd-subtle"
    >
      <header className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-content-tertiary flex items-baseline gap-2">
        <span>Granular</span>
        <span className="text-[9px] text-content-tertiary/70 normal-case">
          audition only
        </span>
      </header>
      <div className="px-3 pb-3 flex flex-col gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={playing ? handleStop : handlePlay}
            disabled={!buffer}
            className={`btn-sm disabled:opacity-50 ${
              playing ? 'btn-secondary' : 'btn-primary'
            }`}
          >
            {playing ? '■ Stop' : '▶ Play grains'}
          </button>
          {/* Mode pills — Chopper vs Stretcher presets. See
                `GRANULAR_PRESETS` in @/lib/granular for the actual
                seeded values; these pills are a one-tap shortcut to
                sensible starting points for the two main personalities
                the GrainPlayer takes on. */}
          <div
            role="radiogroup"
            aria-label="Preview mode"
            className="inline-flex items-center gap-0.5 rounded-md border border-brd-subtle bg-surface-base p-0.5"
            title="Chopper = tight grains + no overlap (jungle chops); Stretcher = longer grains + overlap + loop (conform-to-tempo)"
          >
            {(['chopper', 'stretcher'] as const).map(m => {
              const active = mode === m
              return (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => applyMode(m)}
                  className={`px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-[0.14em] transition-colors ${
                    active
                      ? 'bg-accent text-[color:var(--accent-ink)] font-semibold'
                      : 'text-content-secondary hover:text-content-primary'
                  }`}
                >
                  {m}
                </button>
              )
            })}
          </div>
          <label className="flex items-center gap-2 text-xs text-content-secondary select-none">
            <input
              type="checkbox"
              checked={loop}
              onChange={e => setLoop(e.target.checked)}
              className="accent-[color:var(--accent)]"
            />
            Loop
          </label>
          {slices && slices.length > 0 && (
            <label
              className="flex items-center gap-2 text-xs text-content-secondary select-none"
              title="Snap the position slider to the nearest slice start. Without this, mid-transient retriggers can sound chaotic on a drum break."
            >
              <input
                type="checkbox"
                checked={snapToSlices}
                onChange={e => setSnapToSlices(e.target.checked)}
                className="accent-[color:var(--accent)]"
              />
              Snap to slices ({slices.length})
            </label>
          )}
        </div>

        <Row
          label="Position"
          value={`${snapPosition(positionSec).toFixed(3)}s${
            snapToSlices && slices && slices.length > 0 ? ' · snapped' : ''
          }`}
        >
          <input
            type="range"
            min={0}
            max={max || 0.001}
            step={0.001}
            value={positionSec}
            onChange={e => setPositionSec(Number(e.target.value))}
            disabled={!buffer}
            className="w-full accent-[color:var(--accent)]"
          />
        </Row>

        <Row label="Grain size" value={`${grainSizeMs}ms`}>
          {/* 20 – 300 ms is the usable band for audition grains. */}
          <input
            type="range"
            min={20}
            max={300}
            step={5}
            value={grainSizeMs}
            onChange={e => setGrainSizeMs(Number(e.target.value))}
            disabled={!buffer}
            className="w-full accent-[color:var(--accent)]"
          />
        </Row>

        <Row label="Overlap" value={`${overlapPct}%`}>
          {/* Capped at 50 % — past that grains blur into a drone. */}
          <input
            type="range"
            min={0}
            max={50}
            step={1}
            value={overlapPct}
            onChange={e => setOverlapPct(Number(e.target.value))}
            disabled={!buffer}
            className="w-full accent-[color:var(--accent)]"
          />
        </Row>

        <Row
          label="Pitch"
          value={`${pitchSemitones >= 0 ? '+' : ''}${pitchSemitones} st`}
        >
          <input
            type="range"
            min={-24}
            max={24}
            step={1}
            value={pitchSemitones}
            onChange={e => setPitchSemitones(Number(e.target.value))}
            disabled={!buffer}
            className="w-full accent-[color:var(--accent)]"
          />
        </Row>

        {/* Speed — pitch-preserved tempo change. 100% is native.
              The Stretcher preset lives on this knob: drag to 50%
              and the sample plays at half speed without dropping
              pitch. Independent of the Rubber Band offline warp; use
              this for interactive "what-would-this-sound-like" and
              the Warp button for the pristine committed render. */}
        <Row label="Speed" value={`${speedPct}%`}>
          <input
            type="range"
            min={25}
            max={400}
            step={1}
            value={speedPct}
            onChange={e => setSpeedPct(Number(e.target.value))}
            disabled={!buffer}
            className="w-full accent-[color:var(--accent)]"
          />
        </Row>
      </div>
    </section>
  )
}

const Row: React.FC<{
  label: string
  value: string
  children: React.ReactNode
}> = ({ label, value, children }) => (
  <div>
    <div className="flex items-baseline justify-between mb-1">
      <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
        {label}
      </span>
      <span className="text-[10px] font-mono text-content-secondary tabular-nums">
        {value}
      </span>
    </div>
    {children}
  </div>
)
